import { Dispatch } from 'redux';
import {
  API
} from '@config/index';
import {
  EntityInfo,
  CompositeInfo,
  IntentInfo,
  ParameterConfig,
  ParameterType,
  Attribute,
  entityInfoToLocal,
  intentInfoToLocal,
  compositeInfoToLocal,
  DataQuery,
  ServiceInfo,
  serviceInfoToLocal
} from '@interface/index';

import {
  ParameterMapStore,
  dataQueryToParameter,
  attributeToParameter,
  getFullName
} from '@lib/index';
import { EffectDeclare } from '@lib/model-proxy';
import { AgentParameterCache, defaultState as defaultGlobal } from './index';
import { store } from '@reducer/index';

export const effects: EffectDeclare = {};

let refresh = true;

effects.fetchParameters = ({ id, forceUpdate }: { id: string, forceUpdate?: boolean }) => {
  return async (dispatch: Dispatch) => {

    if (!id || !id.length) {
      setTimeout(() => {
        dispatch({
          payload: {
            agentId: undefined,
            availableChannels: [],
            availableIntents: [],
            availableEntities: [],
            availableComposites: [],
            availableServices: [],
            agentParameterCache: {},
            demoConnected: false
          },
          type: 'global/update_state'
        });
      }, 1000);

      return defaultGlobal;
    }
    const oldState = store.getState().global;

    let entities: EntityInfo[] = [];
    let intents: IntentInfo[] = [];
    let composites: CompositeInfo[] = [];
    let services: ServiceInfo[] = [];
    let demoConnected = oldState.demoConnected;

    if (oldState.agentId !== id) {
      demoConnected = false;
    }


    if (id === oldState.agentId && !forceUpdate && !refresh) {
      entities = oldState.availableEntities;
      intents = oldState.availableIntents;
      composites = oldState.availableComposites;
      services = oldState.availableServices;

    } else {
      ParameterMapStore.clear();
      refresh = false;

      const res: any = await API.agent.availableParameters(id);
      entities = res.agent.available_entities.map(entityInfoToLocal);
      intents = res.agent.available_intents.map(intentInfoToLocal);
      composites = res.agent.available_composites.map(compositeInfoToLocal);
      services = res.agent.available_services.map(serviceInfoToLocal);
    }

    const availableChannels = await API.integration.availableChannels();
    const attrs: Attribute[] = [];
    const dataQueries: DataQuery[] = [];

    const protoMap: any = {};
    const attrPayload: ParameterConfig[] = [];
    const niEntityPayload: ParameterConfig[] = [];
    const curEntityPayload: ParameterConfig[] = [];
    const intentPayload: ParameterConfig[] = [];
    const compositePayload: ParameterConfig[] = [];
    const servicePayload: ParameterConfig[] = [];
    const dataQueryPayload: ParameterConfig[] = [];
    let payload: any = {};

    if (entities && entities.length) {
      entities.forEach((entity: EntityInfo) => {
        if (!entity.id) {
          return;
        }

        protoMap[entity.id] = entity;
        const target = {
          id: entity.id,
          name: getFullName(entity),
          type: ParameterType.ENTITY,
          cuiDisabled: entity.cuiDisabled,
          source: entity.source,
          originKey: entity.originKey,
          promptQuestions: [],
          attributes: (entity.dataQueries || []).map(dataQueryToParameter),
          recognizer: entity.recognizer,
          external: entity.externalQualification,
          triggers: entity.synonym,
          description: entity.description
        };

        curEntityPayload.push(target);

        for (const dq of entity.dataQueries || []) {
          dataQueryPayload.push(dataQueryToParameter(dq));
        }
      });
    }

    if (intents && intents.length) {
      intents.forEach((intent: IntentInfo) => {
        (intent.attributes || []).forEach((attr: any) => {
          if (!attrs.find((item: Attribute) => item.id === attr.id)) {
            attrs.push(attr);
          }
        });

        const target = {
          id: intent.id,
          name: getFullName(intent),
          type: ParameterType.INTENT,
          triggers: intent.triggers,
          originKey: intent.originKey,
          source: intent.source,
          promptQuestions: [],
          inheritedFrames: intent.inheritedFrames,
          external: intent.externalQualification,
          attributes: [...intent.attributes, ...(intent.dataQueries || []).map(dataQueryToParameter)],
          description: intent.description
        };
        intentPayload.push(target);

        for (const dq of intent.dataQueries || []) {
          dataQueryPayload.push(dataQueryToParameter(dq));
        }

      });
    }

    if (composites && composites.length) {
      composites.forEach((composite: CompositeInfo) => {
        if (!composite.id) {
          return;
        }

        (composite.attributes || []).forEach((attr: any) => {
          if (!attrs.find((item: Attribute) => item.id === attr.id)) {
            attrs.push(attr);
          }
        });

        protoMap[composite.id] = composite;
        const target = {
          id: composite.id,
          name: getFullName(composite),
          type: ParameterType.COMPOSITE,
          cuiDisabled: composite.cuiDisabled,
          triggers: composite.triggers,
          source: composite.source,
          originKey: composite.originKey,
          inheritedFrames: composite.inheritedFrames,
          isInterface: composite.isInterface,
          promptQuestions: [],
          isGlobal: composite.isGlobal,
          external: composite.externalQualification,
          attributes: [...composite.attributes, ...(composite.dataQueries || []).map(dataQueryToParameter)],
          description: composite.description
        };
        compositePayload.push(target);

        for (const dq of composite.dataQueries || []) {
          dataQueryPayload.push(dataQueryToParameter(dq));
        }
      });
    }

    if (services && services.length) {
      services.forEach((service: ServiceInfo) => {
        if (!service.id) {
          return;
        }

        (service.attributes || []).forEach((attr: any) => {
          if (!attrs.find((item: Attribute) => item.id === attr.id)) {
            attrs.push(attr);
          }
        });

        protoMap[service.id] = service;
        const target = {
          id: service.id,
          name: getFullName(service),
          type: ParameterType.SERVICE,
          source: service.source,
          originKey: service.originKey,
          promptQuestions: [],
          external: service.externalQualification,
          attributes: [...service.attributes, ...(service.dataQueries || []).map(dataQueryToParameter)]
        };
        servicePayload.push(target);

        for (const dq of service.dataQueries || []) {
          dataQueryPayload.push(dataQueryToParameter(dq));
        }
      });
    }

    if (attrs && attrs.length) {
      attrs.forEach((attr: Attribute) => {
        attrPayload.push(attributeToParameter(attr));
      });
    }

    if (dataQueries && dataQueries.length) {
      dataQueries.forEach((dataQuery: DataQuery) => {
        dataQueryPayload.push(dataQueryToParameter(dataQuery));
      });
    }

    const allParameter = [
      ...attrPayload,
      ...intentPayload,
      ...compositePayload,
      ...servicePayload,
      ...curEntityPayload,
      ...niEntityPayload,
      ...dataQueryPayload
    ];

    setTimeout(() => {
      const kvs: { [key: string]: ParameterConfig } = {};
      allParameter.forEach((parameter: ParameterConfig) => {
        if (parameter.id) {
          kvs[parameter.id] = parameter;
        }
      });

      ParameterMapStore.setMany(kvs);
    }, 1000);

    const agentParameterCache: AgentParameterCache = {
      availableChannels,
      agentId: id,
      availableIntents: intents,
      availableComposites: composites,
      availableEntities: entities,
      availableServices: services
    };


    payload = {
      ...payload,
      ...agentParameterCache,
      demoConnected,
      parameters: allParameter
    };

    setTimeout(() => {
      dispatch({
        payload,
        type: 'global/update_state'
      });
    }, 1000);

    return {
      parameterPayload: payload
    };
  };
};
